// Key/value environment-variable editor shared by the create form and the
// per-sandbox "edit & re-apply" flow. Detects reserved (infra-managed) names
// and blocks them, and supports importing a .env file. All the validation
// logic comes from the model helpers so it stays in lockstep with the backend.
import type { Dispatch, SetStateAction } from 'react';
import {
  createEnvVarEntry,
  isReservedEnvName,
  mergeEnvEntries,
  parseDotEnv,
  reservedEnvKeysInEntries,
  splitReservedEnv,
  type EnvVarEntry,
  type EnvVarScope,
  type ReservedEnvFeatures,
} from '../sandbox/model';
import { color, font } from './tokens';
import { Icon } from './icons';

const inputStyle = (invalid: boolean, padRight = false): React.CSSProperties => ({
  width: '100%',
  background: color.surfaceInset,
  border: `1px solid ${invalid ? 'rgba(224,108,117,.55)' : color.borderInput}`,
  borderRadius: 7,
  padding: padRight ? '8px 28px 8px 10px' : '8px 10px',
  color: invalid ? '#e88b92' : color.textBright,
  fontSize: 12,
  fontFamily: font.mono,
});

export const EnvEditor = ({
  entries,
  setEntries,
  features,
  scope,
  onError,
}: {
  entries: EnvVarEntry[];
  setEntries: Dispatch<SetStateAction<EnvVarEntry[]>>;
  features: ReservedEnvFeatures | undefined;
  scope: EnvVarScope;
  onError?: (message: string | undefined) => void;
}) => {
  const update = (id: string, field: 'key' | 'value', v: string) =>
    setEntries(entries.map(e => (e.id === id ? { ...e, [field]: v } : e)));
  const toggleSecret = (id: string) =>
    setEntries(
      entries.map(e => (e.id === id ? { ...e, isSecret: !e.isSecret } : e)),
    );
  const remove = (id: string) => {
    const next = entries.filter(e => e.id !== id);
    setEntries(next.length > 0 ? next : [createEnvVarEntry()]);
  };
  const add = () => setEntries([...entries, createEnvVarEntry()]);
  const importFile = async (file: File) => {
    try {
      const parsed = parseDotEnv(await file.text());
      if (Object.keys(parsed).length === 0) {
        onError?.(`No variables found in ${file.name}.`);
        return;
      }
      const { allowed, skipped } = splitReservedEnv(parsed, features, scope);
      setEntries(mergeEnvEntries(entries, allowed));
      onError?.(
        skipped.length > 0
          ? `Imported ${Object.keys(allowed).length} variable(s). Skipped ${skipped.length} managed by the infrastructure: ${skipped.join(', ')}.`
          : undefined,
      );
    } catch (e) {
      onError?.(e instanceof Error ? e.message : `Failed to read ${file.name}`);
    }
  };

  const reservedCount = reservedEnvKeysInEntries(entries, features, scope).length;
  const colTemplate = 'minmax(0,1fr) minmax(0,1.3fr) 34px 30px';

  return (
    <div
      style={{
        border: `1px solid ${color.border}`,
        borderRadius: 11,
        background: color.surfaceInset,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 14px',
          borderBottom: `1px solid ${color.borderSoft}`,
        }}
      >
        <span style={{ fontSize: 12.5, fontWeight: 600, color: color.textSecondary }}>
          {scope === 'api' ? 'Backend API variables' : 'Lambda variables'}
        </span>
        <span style={{ fontSize: 11, color: color.textDimmest, fontFamily: font.mono }}>
          → {scope === 'api' ? 'app_env_variables' : 'lambda_settings'}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: color.textDimmer }}>
          Only non-secret values belong here
        </span>
      </div>
      <div style={{ padding: '12px 14px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: colTemplate,
            gap: 8,
            padding: '0 2px 7px',
            font: `600 10px ${font.mono}`,
            letterSpacing: '.08em',
            color: color.textPlaceholder,
          }}
        >
          <span>KEY</span>
          <span>VALUE</span>
          <span style={{ textAlign: 'center' }}>SECRET</span>
          <span />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {entries.map(row => {
            const reserved =
              row.key.trim() !== '' && isReservedEnvName(row.key, features, scope);
            return (
              <div
                key={row.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: colTemplate,
                  gap: 8,
                  alignItems: 'center',
                }}
              >
                <div style={{ position: 'relative' }}>
                  <input
                    value={row.key}
                    placeholder="KEY_NAME"
                    spellCheck={false}
                    onChange={e => update(row.id, 'key', e.target.value)}
                    style={inputStyle(reserved, reserved)}
                  />
                  {reserved && (
                    <span
                      title="Reserved — managed by the platform, will be ignored"
                      style={{
                        position: 'absolute',
                        right: 9,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        display: 'flex',
                      }}
                    >
                      <Icon name="warning" size={12} weight="bold" color={color.red} />
                    </span>
                  )}
                </div>
                <input
                  value={row.value}
                  type={row.isSecret ? 'password' : 'text'}
                  placeholder={row.isSecret ? 'secret value' : 'value'}
                  spellCheck={false}
                  autoComplete="off"
                  onChange={e => update(row.id, 'value', e.target.value)}
                  style={{ ...inputStyle(false), color: color.textSecondary }}
                />
                <button
                  type="button"
                  title={
                    row.isSecret
                      ? 'Secret — masked after save, write-only'
                      : 'Mark as secret (write-only after save)'
                  }
                  onClick={() => toggleSecret(row.id)}
                  style={{
                    width: 34,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: row.isSecret ? 'rgba(123,116,240,.16)' : 'none',
                    border: `1px solid ${
                      row.isSecret ? 'rgba(123,116,240,.5)' : color.borderSoft
                    }`,
                    borderRadius: 7,
                    color: row.isSecret ? color.accentSoft : color.textDimmer,
                    cursor: 'pointer',
                  }}
                >
                  <Icon name="lock" size={13} weight={row.isSecret ? 'fill' : 'regular'} />
                </button>
                <button
                  type="button"
                  title="Remove"
                  onClick={() => remove(row.id)}
                  style={{
                    width: 30,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'none',
                    border: `1px solid ${color.borderSoft}`,
                    borderRadius: 7,
                    color: color.textDimmer,
                    cursor: 'pointer',
                  }}
                >
                  <Icon name="trash" size={13} />
                </button>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
          <button
            type="button"
            onClick={add}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              height: 31,
              padding: '0 12px',
              background: color.surfaceChip,
              border: `1px solid ${color.borderInput}`,
              borderRadius: 7,
              color: color.textSecondary,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            <Icon name="plus" size={12} weight="bold" />
            Add variable
          </button>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              height: 31,
              padding: '0 12px',
              background: 'none',
              border: `1px solid ${color.borderInput}`,
              borderRadius: 7,
              color: color.textSecondary,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            <Icon name="upload" size={13} />
            Import .env
            <input
              type="file"
              hidden
              accept=".env,text/plain"
              onChange={e => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void importFile(file);
              }}
            />
          </label>
          {reservedCount > 0 && (
            <span
              style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 11,
                color: color.red,
              }}
            >
              <Icon name="warning" size={12} weight="bold" />
              {reservedCount} reserved variable{reservedCount > 1 ? 's' : ''} will be
              blocked
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
